// src/utils/googleTranslate.ts
//
// מקור אמת יחיד לווידג'ט Google Translate.
//
// ── למה המודול הזה קיים ─────────────────────────────────────────────
// היו שלושה מימושים נפרדים — LandingPage, EnduserLayout ו-AuthPage — וכל
// אחד מהם טען בעצמו את element.js, ושניים מהם אף פתחו ב:
//
//     document.querySelectorAll('script[src*="translate.google.com"]')
//       .forEach(s => s.remove())
//
// כלומר כל אחד הרס את הסקריפט של השני, ושניים מהם אף השתמשו באותו מזהה
// DOM. הבעיה האמיתית: element.js לא סובל טעינה כפולה באותו דף — מודול
// הליבה el_main נטען בשתי גרסאות שונות ונשבר עם
// "TypeError: _.ca.Be is not a function", והבורר לא נבנה כלל.
//
// ── שני הכללים כאן ──────────────────────────────────────────────────
// 1. הסקריפט נטען **פעם אחת בחיי הדף** ולעולם לא מוסר.
// 2. הווידג'ט נבנה **פעם אחת בחיי הדף** ואחר כך רק מוזז בין קונטיינרים.
//
// הכלל השני התגלה אחרי שהראשון לבדו לא הספיק: הבורר חזר בדף הבית אבל
// לא בדשבורד. בדיקה בפרודקשן הראתה ש-TranslateElement שני לא זורק שגיאה
// ופשוט לא מייצר כלום — גם לתוך קונטיינר חדש. לעומת זאת הזזה של
// האלמנט הקיים עובדת במלואה, כולל תרגום בפועל אחרי ההזזה.
//
// לכן ה-cleanup **מנתק** את הווידג'ט ולא מוחק אותו: innerHTML = "" היה
// הורס אותו לתמיד, בלי דרך לשחזר עד רענון מלא של הדף.

const SCRIPT_ID = "cartax-google-translate-script";
const STYLE_ID = "cartax-google-translate-styles";
const READY_CALLBACK = "__cartaxGoogleTranslateReady";

// ── למה אין כאן אפשרויות פר-קריאה ──────────────────────────────────
// google.translate.TranslateElement נבנה **פעם אחת בחיי הדף**. בנייה
// שנייה לא זורקת שגיאה ופשוט לא עושה כלום — גם לתוך קונטיינר חדש לגמרי
// (נבדק בפרודקשן). לכן אי אפשר לתת לכל מסך פריסה ורשימת שפות משלו:
// המסך הראשון שנטען היה קובע לכולם, וזה היה יוצא לא צפוי.
// במקום זה יש קונפיגורציה אחת לכל האתר, והווידג'ט **מוזז** בין
// הקונטיינרים במקום להיבנות מחדש. אומת: אחרי הזזה הבורר מתפקד ומתרגם.
const LANGUAGES = "en,ar,ru,fr,es,de,it,pt,zh,ja,he";
const LAYOUT = "HORIZONTAL";

// שמות השפות בשפת המקור. גוגל מציג אותם בעברית, ודובר רוסית לא מזהה
// את המילה "רוסית" — לכן ההחלפה.
const NATIVE_NAMES: Record<string, string> = {
  "": "Language",
  en: "English",
  ar: "العربية",
  ru: "Русский",
  fr: "Français",
  es: "Español",
  de: "Deutsch",
  it: "Italiano",
  pt: "Português",
  zh: "中文",
  ja: "日本語",
  he: "עברית",
};

// התוויות שגוגל מציג בתפריט ה-SIMPLE, שאין להן value לזהות לפיו
const HEBREW_LABEL_TO_NATIVE: Record<string, string> = {
  אנגלית: "English",
  ערבית: "العربية",
  רוסית: "Русский",
  צרפתית: "Français",
  ספרדית: "Español",
  גרמנית: "Deutsch",
  איטלקית: "Italiano",
  פורטוגזית: "Português",
  סינית: "中文",
  יפנית: "日本語",
};

// ────────────────────────────────────────────────
// טעינת ה-API — פעם אחת בלבד
// ────────────────────────────────────────────────
let apiPromise: Promise<void> | null = null;

function loadApi(): Promise<void> {
  if (apiPromise) return apiPromise;

  apiPromise = new Promise<void>((resolve, reject) => {
    const w = window as any;

    // כבר נטען — למשל מ-HTML סטטי ישן שנשמר במטמון הדפדפן.
    // חשוב: לא טוענים שוב, זו בדיוק הטעות שגרמה לתקלה.
    if (w.google?.translate?.TranslateElement) {
      resolve();
      return;
    }

    // נרשמים לפני בדיקת הסקריפט: אם טעינה קודמת כבר בדרך, ה-callback
    // שלה יפעיל את ה-resolve הזה. בלי זה ה-promise היה נתקע לנצח.
    w[READY_CALLBACK] = () => resolve();

    if (document.getElementById(SCRIPT_ID)) return;

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `//translate.google.com/translate_a/element.js?cb=${READY_CALLBACK}`;
    script.onerror = () => {
      apiPromise = null; // מאפשר ניסיון חוזר בניווט הבא
      reject(new Error("Google Translate script failed to load"));
    };
    document.head.appendChild(script);
  });

  return apiPromise;
}

// ────────────────────────────────────────────────
// סגנונות — מוזרקים פעם אחת
// ────────────────────────────────────────────────
// אוחדו מארבעה מקומות שונים (index.css, LandingPage, EnduserLayout,
// AuthPage). הבסיס הוא הגרסה של EnduserLayout, שהייתה המקיפה ביותר.
// לשים לב: EnduserLayout יצר <style> חדש בכל mount ולא הסיר אותו לעולם,
// כך שכל ניווט הוסיף עותק נוסף ל-head.
function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
/* הסתרת "Powered by Google Translate" */
.goog-te-gadget > span,
.goog-te-gadget-simple > span,
.goog-te-gadget > div > span,
.VIpgJd-ZVi9od-l4eHX-hSRGPd,
.VIpgJd-ZVi9od-ORHb-OEVmcd {
  display: none !important;
}

/* הסתרת הלוגו של גוגל */
.goog-te-gadget img,
.goog-te-gadget-simple img {
  display: none !important;
}

/* הבאנר העליון שגוגל מוסיף, והבועות */
.goog-te-banner-frame,
.goog-te-banner-frame.skiptranslate,
iframe.goog-te-banner-frame,
.goog-te-balloon-frame,
.goog-te-spinner-pos {
  display: none !important;
  height: 0 !important;
  visibility: hidden !important;
}

/* גוגל דוחף את ה-body כלפי מטה כשהבאנר מוצג */
body { top: 0 !important; }

/* הבורר עצמו */
.goog-te-combo {
  display: inline-block !important;
  padding: 8px 16px !important;
  border: 2px solid #e5e7eb !important;
  border-radius: 8px !important;
  font-size: 14px !important;
  font-family: 'Segoe UI', sans-serif !important;
  font-weight: 500 !important;
  background: white !important;
  color: #374151 !important;
  min-width: 110px !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
  transition: all 0.2s ease !important;
  cursor: pointer !important;
  margin: 0 !important;
}

.goog-te-combo:hover {
  border-color: #3b82f6 !important;
  background: #f8fafc !important;
  box-shadow: 0 4px 12px rgba(59,130,246,0.15) !important;
}

.goog-te-combo:focus {
  outline: none !important;
  border-color: #2563eb !important;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1) !important;
}

.goog-te-gadget {
  font-family: inherit !important;
  font-size: 14px !important;
  line-height: 1 !important;
  display: flex !important;
  align-items: center !important;
  margin: 0 !important;
  padding: 0 !important;
}

.goog-te-gadget-simple {
  border: none !important;
  background: transparent !important;
  padding: 0 !important;
  margin: 0 !important;
  display: flex !important;
  align-items: center !important;
}

.goog-te-gadget-simple .goog-te-menu-value {
  color: #374151 !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  display: flex !important;
  align-items: center !important;
  margin: 0 !important;
  padding: 0 !important;
}

.goog-te-gadget-simple .goog-te-menu-value::before { content: none !important; }

/* ── אזור אישי: יישור לימין + אייקון ── */
.google-translate-container {
  direction: ltr;
  min-width: 140px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.google-translate-container .goog-te-gadget,
.google-translate-container .goog-te-gadget-simple {
  justify-content: flex-end !important;
  text-align: right !important;
}

.google-translate-container .goog-te-gadget-simple::before {
  content: "🌍";
  margin-left: 6px;
  font-size: 16px;
  display: inline-block;
}

/* ── דף הבית: בורר ממורכז וגוון מותג ── */
.google-translate-landing { direction: ltr; text-align: center; }

.google-translate-landing .goog-te-combo {
  padding: 6px 12px !important;
  min-width: auto !important;
}

.google-translate-landing .goog-te-combo:hover {
  border-color: #003B7B !important;
  background: white !important;
  box-shadow: 0 4px 12px rgba(0,59,123,0.15) !important;
}

@media (max-width: 640px) {
  .google-translate-container { min-width: 100px; }
  .goog-te-combo {
    font-size: 12px !important;
    padding: 6px 12px !important;
    min-width: 90px !important;
  }
  .google-translate-container .goog-te-gadget-simple::before {
    font-size: 14px;
    margin-left: 4px;
  }
}
`;
  document.head.appendChild(style);
}

// ────────────────────────────────────────────────
// החלפת שמות השפות לשפת המקור
// ────────────────────────────────────────────────
// אידמפוטנטי בכוונה: גוגל בונה מחדש את התפריט מדי פעם ומחזיר את השמות
// לעברית, וה-observer מגיב לכל שינוי. בלי בדיקת "כבר נכון" היינו נכנסים
// ללולאת שינויים אינסופית.
function applyNativeNames(): void {
  const combo = document.querySelector<HTMLSelectElement>(".goog-te-combo");
  if (combo) {
    Array.from(combo.options).forEach((option) => {
      const native = NATIVE_NAMES[option.value];
      if (native && option.textContent?.trim() !== native) {
        option.textContent = native;
      }
    });
  }

  // פריסת SIMPLE: תווית הכפתור ופריטי התפריט הנפתח
  const label = document.querySelector(".goog-te-menu-value span:first-child");
  if (label?.textContent?.includes("בחר")) {
    label.textContent = "Language";
  }

  document.querySelectorAll(".goog-te-menu2-item span").forEach((item) => {
    const native = HEBREW_LABEL_TO_NATIVE[(item.textContent || "").trim()];
    if (native) item.textContent = native;
  });
}

// ────────────────────────────────────────────────
// API ציבורי
// ────────────────────────────────────────────────
// הווידג'ט החי, אחרי שנבנה. נשמר כדי שנוכל להזיז אותו בין מסכים
// במקום לבנות מחדש — בנייה שנייה לא עובדת.
let widgetHost: HTMLElement | null = null;

/**
 * תולה את בורר השפות בקונטיינר הנתון.
 * מחזיר פונקציית ניקוי שיש לקרוא לה ב-unmount.
 */
export function mountGoogleTranslate(containerId: string): () => void {
  let cancelled = false;
  let observer: MutationObserver | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  injectStyles();

  const watchNames = (built: Element) => {
    applyNativeNames();
    observer = new MutationObserver(() => applyNativeNames());
    observer.observe(built.parentElement || document.body, {
      childList: true,
      subtree: true,
    });
  };

  loadApi()
    .then(() => {
      if (cancelled) return;
      const container = document.getElementById(containerId);
      if (!container) return;

      // כבר נבנה בעבר — מזיזים את אותו אלמנט לכאן.
      // חשוב: appendChild מנתק אותו מההורה הקודם אוטומטית.
      if (widgetHost) {
        container.appendChild(widgetHost);
        const built = widgetHost.querySelector(".goog-te-combo, .goog-te-menu-value");
        if (built) watchNames(built);
        return;
      }

      const w = window as any;
      try {
        new w.google.translate.TranslateElement(
          {
            pageLanguage: "he",
            includedLanguages: LANGUAGES,
            layout: w.google.translate.TranslateElement.InlineLayout[LAYOUT],
            autoDisplay: false,
            multilanguagePage: true,
          },
          containerId
        );
      } catch (error) {
        console.error("Google Translate init error:", error);
        return;
      }

      // ממתינים שגוגל יבנה את התפריט, שומרים הפניה, ומשמרים את השמות.
      // תקרת ניסיונות כדי שלא נסקר לנצח אם הווידג'ט לא נבנה.
      let attempts = 0;
      pollTimer = setInterval(() => {
        if (cancelled) return;
        const built = container.querySelector(".goog-te-combo, .goog-te-menu-value");
        if (!built) {
          if (++attempts > 200 && pollTimer) {
            clearInterval(pollTimer);
            pollTimer = null;
          }
          return;
        }
        if (pollTimer) {
          clearInterval(pollTimer);
          pollTimer = null;
        }
        widgetHost = container.firstElementChild as HTMLElement | null;
        watchNames(built);
      }, 100);
    })
    .catch((error) => {
      console.error("Google Translate load error:", error);
    });

  return () => {
    cancelled = true;
    if (pollTimer) clearInterval(pollTimer);
    if (observer) observer.disconnect();
    // מנתקים את הווידג'ט מה-DOM אבל **שומרים** את ההפניה אליו.
    // ניקוי ב-innerHTML היה הורס אותו לתמיד, ובנייה מחדש לא אפשרית.
    // הסקריפט של גוגל נשאר טעון בכוונה.
    if (widgetHost && widgetHost.parentElement?.id === containerId) {
      widgetHost.remove();
    }
  };
}
